import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { projectsData } from '../utils/constants';
import { FaExternalLinkAlt, FaGithub } from 'react-icons/fa';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <section id="projects" className="section-padding bg-bg-secondary">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black gradient-text mb-6">
            Featured Work
          </h2>
          <p className="text-lg sm:text-xl text-text-secondary font-light max-w-2xl mx-auto">
            Real-world applications built with modern tech stack
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
        >
          {projectsData.map((project, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                y: -15,
                scale: 1.02,
                borderColor: 'var(--accent-primary)',
              }}
              className="bg-bg-tertiary rounded-3xl overflow-hidden border border-[rgba(76,201,240,0.1)] transition-all duration-500 hover:shadow-[0_25px_60px_rgba(76,201,240,0.25)] group"
            >
              <div className="relative w-full h-56 overflow-hidden">
                <motion.img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.15 }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(15,14,23,0.8)] to-transparent" />
              </div>

              <div className="p-6">
                <h3 className="text-xl sm:text-2xl font-bold text-text-primary mb-4 transition-colors duration-300 group-hover:text-accent-primary">
                  {project.title}
                </h3>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.stack.map((tech, techIndex) => (
                    <motion.span
                      key={techIndex}
                      whileHover={{ y: -2, backgroundColor: 'rgba(76, 201, 240, 0.2)' }}
                      className="px-4 py-2 bg-[rgba(76,201,240,0.1)] text-accent-primary rounded-full text-xs font-medium transition-all duration-300"
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>

                <div className="flex gap-4 flex-wrap">
                  <motion.a
                    href={project.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{
                      y: -2,
                      backgroundColor: 'var(--accent-primary)',
                      color: 'var(--bg-primary)',
                    }}
                    className="px-6 py-3 bg-transparent text-text-primary border border-accent-primary rounded-full text-sm font-medium transition-all duration-300 hover:shadow-[0_5px_15px_rgba(76,201,240,0.3)] flex items-center gap-2"
                  >
                    <FaExternalLinkAlt />
                    Live Demo
                  </motion.a>
                  <motion.a
                    href={project.codeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{
                      y: -2,
                      backgroundColor: 'var(--accent-primary)',
                      color: 'var(--bg-primary)',
                    }}
                    className="px-6 py-3 bg-transparent text-text-primary border border-accent-primary rounded-full text-sm font-medium transition-all duration-300 hover:shadow-[0_5px_15px_rgba(76,201,240,0.3)] flex items-center gap-2"
                  >
                    <FaGithub />
                    GitHub
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Projects;
